import React, { useContext, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Circle, Save, Upload } from 'lucide-react';
import { adminAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import { buildTenantAppBaseUrl } from '../../utils/environmentRouting';
import OnboardingTimezoneSelect from './OnboardingTimezoneSelect';
import { OnboardingDraftRecoveryContext } from './onboardingDraftRecovery';
import {
  contrastOnWhite, onboardingKey, onboardingTaskHref, OnboardingBranding,
  OnboardingProfile, OnboardingStatus, ONBOARDING_BEFORE_LEAVE, safeImageUrl, validateBranding, validateProfile,
} from './onboardingWorkspace';

const stripeConfigurationLabels: Record<string, string> = {
  dev: 'DEV configuration', prod: 'Production configuration', test: 'Test mode', live: 'Live mode',
};

export default function AdminOnboarding() {
  const { operationsCompanyId, user } = useAuth();
  const companyId = operationsCompanyId || user?.company_id;
  return <OnboardingWorkspace key={companyId || 'unscoped'} companyId={companyId} />;
}

function OnboardingWorkspace({ companyId }: { companyId?: string }) {
  const recovery = useContext(OnboardingDraftRecoveryContext);
  const [recovered] = useState(() => recovery?.read() || null);
  const location = useLocation();
  const operations = location.pathname.startsWith('/operations/');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const client = useQueryClient();
  const key = onboardingKey(companyId);
  const [profile, setProfile] = useState<OnboardingProfile | null>(recovered?.profile || null);
  const [branding, setBranding] = useState<OnboardingBranding | null>(recovered?.branding || null);
  const [profileBase, setProfileBase] = useState<OnboardingProfile | null>(recovered?.profileBase || null);
  const [brandingBase, setBrandingBase] = useState<OnboardingBranding | null>(recovered?.brandingBase || null);
  const [revision, setRevision] = useState(recovered?.revision || '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [writing, setWriting] = useState(false);
  const partialProfileSaved = useRef(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const dirty = JSON.stringify(profile) !== JSON.stringify(profileBase) || JSON.stringify(branding) !== JSON.stringify(brandingBase);
  const dirtyRef = useRef(dirty);
  const busyRef = useRef(false);
  dirtyRef.current = dirty;
  const query = useQuery(key, () => adminAPI.getOnboardingStatus(), {
    enabled: Boolean(companyId), select: (response) => response.data,
    refetchOnWindowFocus: !writing, refetchOnReconnect: !writing,
  });
  const status = query.data;
  const task = status?.tasks.find((item) => item.id === params.get('task')) || status?.tasks[0];
  const home = operations ? '/operations/assistant' : '/admin';
  const assistant = operations ? '/operations/assistant' : '/admin/assistant';

  useEffect(() => {
    if (profile && profileBase && branding && brandingBase && revision) {
      recovery?.write(dirty ? { profile, profileBase, branding, brandingBase, revision } : null);
    }
  }, [recovery, dirty, profile, profileBase, branding, brandingBase, revision]);

  useEffect(() => {
    // Keep the original revision with dirty drafts so concurrent writes produce a conflict.
    if (!status || dirty || writing) return;
    setProfile(status.profile); setProfileBase(status.profile);
    setBranding(status.branding); setBrandingBase(status.branding);
    setRevision(status.revision);
  }, [status, dirty, writing]);

  useEffect(() => {
    const leaving = (event: Event) => {
      if (busyRef.current) {
        event.preventDefault(); setMessage('Wait for the current setup operation to finish before leaving.');
      } else if (dirtyRef.current) {
        if (!window.confirm('Discard unsaved setup changes?')) event.preventDefault();
        else recovery?.write(null);
      }
    };
    const unload = (event: BeforeUnloadEvent) => {
      if (dirtyRef.current) { event.preventDefault(); event.returnValue = ''; }
    };
    const link = (event: MouseEvent) => {
      const anchor = (event.target as Element).closest?.('a[href]');
      if (anchor && (busyRef.current || (dirtyRef.current && !window.confirm('Discard unsaved setup changes?')))) {
        event.preventDefault(); event.stopPropagation();
      } else if (anchor) recovery?.write(null);
    };
    window.addEventListener('beforeunload', unload);
    window.addEventListener(ONBOARDING_BEFORE_LEAVE, leaving);
    document.addEventListener('click', link, true);
    return () => { window.removeEventListener('beforeunload', unload); window.removeEventListener(ONBOARDING_BEFORE_LEAVE, leaving); document.removeEventListener('click', link, true); };
  }, [recovery]);

  const accept = (next: OnboardingStatus) => {
    setProfile(next.profile); setProfileBase(next.profile);
    setBranding(next.branding); setBrandingBase(next.branding);
    setRevision(next.revision);
    dirtyRef.current = false;
    client.setQueryData(key, { data: next });
  };
  const beginWrite = async () => {
    busyRef.current = true;
    setWriting(true);
    await client.cancelQueries(key);
  };
  const commitWrite = async (next: OnboardingStatus) => {
    // Discard any read started before the write's refreshed status was received.
    await client.cancelQueries(key);
    client.setQueryData(key, { data: next });
  };
  const save = useMutation(async () => {
    partialProfileSaved.current = false;
    if (!profile || !branding) throw new Error('Setup has not loaded.');
    const profileChanged = JSON.stringify(profile) !== JSON.stringify(profileBase);
    const brandingChanged = JSON.stringify(branding) !== JSON.stringify(brandingBase);
    const trimmed = Object.fromEntries(Object.entries(profile).map(([name, value]) => [name, value.trim()])) as unknown as OnboardingProfile;
    const validation = { ...(profileChanged ? validateProfile(trimmed) : {}), ...(brandingChanged ? validateBranding(branding) : {}) };
    setErrors(validation);
    if (Object.keys(validation).length) throw new Error('Check the highlighted fields.');
    await beginWrite();
    let next = status!;
    let nextRevision = revision;
    if (profileChanged) {
      next = (await adminAPI.updateOnboardingProfile({ ...trimmed, expected_revision: nextRevision })).data;
      partialProfileSaved.current = true;
      // Preserve the branding draft even if the second save fails.
      setProfile(next.profile); setProfileBase(next.profile); setRevision(next.revision);
      await commitWrite(next);
      nextRevision = next.revision;
    }
    if (brandingChanged) {
      next = (await adminAPI.updateOnboardingBranding({ ...branding, expected_revision: nextRevision })).data;
      await commitWrite(next);
    }
    return next;
  }, {
    onSuccess: (next) => { accept(next); setMessage('Changes saved.'); },
    onError: (error: any) => {
      const fields = error.response?.data?.fields;
      if (fields) setErrors(Object.fromEntries(Object.entries(fields).map(([name, value]) => [name, Array.isArray(value) ? value.join(' ') : String(value)])));
      const detail = error.response?.status === 409
        ? 'Setup changed elsewhere. Your edits are preserved. Reload saved values to review the latest version.'
        : error.response?.data?.message?.toString() || error.message || 'Save failed. Please retry.';
      setMessage((partialProfileSaved.current ? 'Business details saved. Branding changes are still unsaved. ' : '') + detail);
    },
    onSettled: () => setWriting(false),
  });
  const finish = useMutation(async () => {
    await beginWrite();
    const response = await adminAPI.completeOnboarding(revision);
    await commitWrite(response.data);
    return response;
  }, {
    onSuccess: (response) => { accept(response.data); setMessage('Setup complete. Your workspace is ready.'); },
    onError: () => setMessage('Could not finish setup. Check business basics or reload the latest saved values and retry.'),
    onSettled: () => setWriting(false),
  });
  const later = useMutation(async () => {
    await beginWrite();
    const response = await adminAPI.dismissOnboarding();
    await commitWrite(response.data);
    return response;
  }, {
    onSuccess: (response) => { accept(response.data); navigate(home); },
    onError: () => setMessage('Could not continue later. Please retry.'),
    onSettled: () => setWriting(false),
  });
  const busy = save.isLoading || finish.isLoading || later.isLoading || uploading || reloading || writing;
  busyRef.current = busy;
  const discard = () => !dirty || window.confirm('Discard unsaved setup changes?');
  const selectTask = (id: string) => {
    if (!discard() || busy) return;
    if (status) accept(status);
    setErrors({}); setMessage('');
    const next = new URLSearchParams(params); next.set('task', id); setParams(next);
  };
  const reload = async () => {
    if (!discard() || busy) return;
    setReloading(true);
    busyRef.current = true;
    try {
      const result = await query.refetch();
      if (result.isError || !result.data) setMessage('Reload failed. Your edits are preserved.');
      else { accept(result.data); setErrors({}); setMessage('Saved values loaded.'); }
    } finally { setReloading(false); }
  };
  const upload = async (file?: File) => {
    if (!file || busy) return;
    if (!file.type.startsWith('image/') || file.size > 2 * 1024 * 1024) {
      setMessage('Choose an image under 2 MB.'); return;
    }
    setUploading(true);
    busyRef.current = true;
    try {
      const response = await adminAPI.uploadOnboardingLogo(file);
      setBranding((value) => value ? { ...value, logo_url: response.data.url } : value);
      setMessage('Logo uploaded. Save changes to apply it.');
    } catch { setMessage('Logo upload failed. Please retry.'); }
    finally { setUploading(false); if (fileInput.current) fileInput.current.value = ''; }
  };

  if (!companyId) return <p role="alert" className="p-6">Select a business to open setup.</p>;
  if (query.isLoading) return <div role="status" aria-label="Loading setup"><LoadingSpinner className="py-20" /></div>;
  if (!status || !profile || !branding) return <section className="p-6" role="alert"><h1 className="text-xl font-semibold">Setup unavailable</h1><p>We could not load this business.</p><button className="btn-secondary mt-4" onClick={() => void query.refetch()}>Retry</button></section>;
  const field = (name: keyof OnboardingProfile, label: string, maxLength: number) => <label key={name} className="block text-sm font-medium">
    {label}<input aria-label={label} className="input-field w-full mt-1" type={name === 'supportEmail' ? 'email' : 'text'} maxLength={maxLength} value={profile[name]} disabled={busy}
      aria-invalid={Boolean(errors[name])} aria-describedby={errors[name] ? `${name}-error` : undefined}
      onChange={(event) => setProfile({ ...profile, [name]: event.target.value })} />
    {errors[name] && <span id={`${name}-error`} className="block text-red-700 mt-1">{errors[name]}</span>}
  </label>;
  const taskHref = task ? onboardingTaskHref(task.href, operations) : null;
  let tenantPaymentSettings: string | null = null;
  if (operations && status.company.id === companyId && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(status.company.slug) && !['globaladmin', 'api', 'www'].includes(status.company.slug)) {
    const base = buildTenantAppBaseUrl(status.company.slug);
    if (new URL(base).hostname.startsWith(`${status.company.slug}.`)) tenantPaymentSettings = `${base}/admin/settings?tab=stripe`;
  }
  return <div className="max-w-6xl mx-auto p-4 sm:p-6 text-on-surface">
    <header className="border-b pb-4 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3"><h1 className="text-2xl font-semibold">Business setup</h1><span className="text-sm">{status.company.name} / {status.environment}</span></div>
      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-sm" aria-live="polite"><span>Workspace: {status.workspace_ready ? 'Ready' : 'Business basics needed'}</span><span>Payments: {status.payment_ready ? 'Ready' : 'Not ready'}</span></div>
      <p className="mt-2 text-sm text-on-surface-variant">{status.saved_at ? `Last saved ${new Date(status.saved_at).toLocaleString()}` : 'No setup changes saved yet.'}</p>
    </header>
    {query.isError && <p role="alert" className="mb-4">Could not refresh setup. Saved values may have changed. <button onClick={() => void reload()} className="underline">Retry</button></p>}
    <div className="grid md:grid-cols-[15rem_minmax(0,1fr)] gap-6">
      <nav aria-label="Setup checklist" className="min-w-0"><ol className="divide-y">{status.tasks.map((item) => <li key={item.id}>
        <button type="button" disabled={busy} aria-current={task?.id === item.id ? 'step' : undefined} onClick={() => selectTask(item.id)} className={`w-full text-left py-3 px-2 flex gap-2 rounded ${task?.id === item.id ? 'bg-surface-container font-semibold' : ''}`}>
          {item.status === 'complete' ? <CheckCircle2 size={20} className="shrink-0 text-green-700" aria-hidden="true" /> : <Circle size={20} className="shrink-0" aria-hidden="true" />}<span className="min-w-0 break-words">{item.title}<span className="block text-xs mt-1 font-normal">{item.status} / {item.required ? 'Required' : 'Optional'}</span></span>
        </button></li>)}</ol></nav>
      <section aria-labelledby="setup-task-title" className="min-w-0">
        <h2 id="setup-task-title" className="text-xl font-semibold">{task?.title}</h2><p className="text-sm mt-2 mb-5 text-on-surface-variant">{task?.description}</p>
        {task && <p className="text-xs mb-4">Owner: {task.owner === 'operations' ? 'MODAL Operations' : 'Business operator'}{task.last_updated_at ? ` / Updated ${new Date(task.last_updated_at).toLocaleString()}` : ''}</p>}
        <fieldset disabled={busy}>
          {task?.id === 'company_info' && <div className="grid sm:grid-cols-2 gap-4">{field('companyName', 'Business name', 200)}{field('supportEmail', 'Support email', 256)}<div className="sm:col-span-2">{field('companyAddress', 'Business address', 255)}</div>{field('invoicePrefix', 'Invoice prefix', 20)}<OnboardingTimezoneSelect value={profile.timezone} disabled={busy} error={errors.timezone} onChange={(timezone) => setProfile({ ...profile, timezone })} /></div>}
          {task?.id === 'branding' && <div className="space-y-4">
            <p className="text-sm">Branding is optional. Business basics are enough to open your workspace.</p>
            {(['app_name', 'logo_url', 'favicon_url'] as const).map((name) => <label key={name} className="block text-sm font-medium">{{ app_name: 'Display name', logo_url: 'Logo URL', favicon_url: 'Favicon URL' }[name]}<input className="input-field w-full mt-1" value={branding[name]} maxLength={name === 'app_name' ? 200 : 1000} aria-invalid={Boolean(errors[name])} aria-describedby={errors[name] ? `${name}-error` : undefined} onChange={(event) => setBranding({ ...branding, [name]: event.target.value })} />{errors[name] && <span id={`${name}-error`} className="block text-red-700">{errors[name]}</span>}</label>)}
            <input ref={fileInput} type="file" accept="image/*" aria-label="Upload business logo" className="sr-only" onChange={(event) => void upload(event.target.files?.[0])} />
            <button type="button" className="btn-secondary inline-flex items-center gap-2" onClick={() => fileInput.current?.click()}><Upload size={16} aria-hidden="true" />{uploading ? 'Uploading...' : 'Upload logo'}</button>
            <div className="flex flex-wrap gap-6">{(['primary_color', 'secondary_color'] as const).map((name) => <label key={name} className="text-sm">{name === 'primary_color' ? 'Primary color' : 'Secondary color'}<input type="color" className="block w-16 h-10 mt-1" value={/^#[0-9a-f]{6}$/i.test(branding[name]) ? branding[name] : '#000000'} onChange={(event) => setBranding({ ...branding, [name]: event.target.value })} />{errors[name] && <span className="text-red-700">{errors[name]}</span>}</label>)}</div>
            {(contrastOnWhite(branding.primary_color) < 4.5 || contrastOnWhite(branding.secondary_color) < 4.5) && <p role="status" className="text-sm text-amber-800">Contrast warning: one or more colors may make white text difficult to read.</p>}
            <div className="grid sm:grid-cols-2 gap-4">{['Quote', 'Invoice'].map((label) => <article aria-label={`${label} preview`} key={label} className="border rounded p-4 bg-white text-gray-900 overflow-hidden">
              {branding.logo_url && safeImageUrl(branding.logo_url) && <img src={branding.logo_url} alt="Business logo preview" className="h-16 w-full object-contain object-left mb-3" />}
              <h3 className="font-semibold break-words">{branding.app_name || profile.companyName || status.company.name}</h3><p className="text-xs whitespace-pre-line break-words">{profile.companyAddress}</p><hr className="my-3" style={{ borderColor: branding.secondary_color }} /><p className="font-semibold" style={{ color: branding.primary_color }}>{label} {profile.invoicePrefix}-0001</p><p className="text-sm mt-3">Sample trip service</p><p className="text-sm mt-2">Total $100.00</p>
            </article>)}</div>
          </div>}
          {task?.id === 'stripe' && <div className="space-y-3 text-sm">
            <h3 className="font-semibold">Payment readiness</h3>
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 break-words">
              <dt>Business</dt><dd>{status.company.name} ({status.company.slug})</dd>
              <dt>Environment</dt><dd>{status.environment}</dd>
              <dt>Stripe configuration</dt><dd>{stripeConfigurationLabels[status.payment_health.stripe_mode || ''] || 'Unknown'}</dd>
              <dt>Connected account</dt><dd>{status.payment_health.connected_account_suffix ? `Ending ${status.payment_health.connected_account_suffix}` : 'Not available'}</dd>
            </dl>
            <p>{status.payment_ready ? 'Payments are ready.' : 'Payments are not ready. Charges, payouts and a current healthy account snapshot are required.'}</p>
            <p>Health: {status.payment_health.status.replace(/_/g, ' ')}</p>
            {status.payment_health.reasons.length > 0 && <ul className="list-disc pl-5">{status.payment_health.reasons.map((reason, index) => <li key={index}>{reason}</li>)}</ul>}
            <p>{status.payment_health.checked_at ? `Checked ${new Date(status.payment_health.checked_at).toLocaleString()}` : 'No health snapshot available.'}</p>
            <p>You can finish business setup before payments are ready.</p>
            {operations ? <>
              <p>A business administrator must sign in to the tenant workspace and explicitly authorize any Stripe connection or payment settings changes.</p>
              {tenantPaymentSettings ? <a className="underline" href={tenantPaymentSettings} target="_blank" rel="noopener noreferrer">Open {status.company.name} payment settings</a> : <p>Payment settings are available in this business's tenant workspace after administrator sign-in.</p>}
            </> : <Link className="underline" to="/admin/settings?tab=stripe">Review payment settings</Link>}
          </div>}
          {(task?.id === 'team' || task?.id === 'first_task') && <div className="space-y-3 text-sm">{taskHref ? <Link className="underline inline-flex gap-2 items-center" to={taskHref}>Open {task.title}<ArrowRight size={16} aria-hidden="true" /></Link> : <p>This task requires the business administration workspace or Operations assistance.</p>}</div>}
          {task?.id === 'first_task' && !operations && <nav className="mt-4 flex flex-wrap gap-x-5 gap-y-3 text-sm" aria-label="Optional business preparation"><Link className="underline" to="/admin/invoice-import">Import invoices</Link><Link className="underline" to="/admin/settings?tab=quoteTerms">Review quote terms</Link><Link className="underline" to="/admin/quotes">Open quotes</Link></nav>}
        </fieldset>
        <p className="mt-6 text-sm"><Link to={assistant} className="underline">Open operator assistant</Link></p>
      </section>
    </div>
    <footer className="sticky bottom-0 z-10 mt-8 border-t bg-surface py-4 flex flex-wrap items-center gap-3" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
      <div className="w-full text-sm" aria-live="polite">{message || (dirty ? 'Unsaved changes' : 'All changes saved')}</div>
      <button type="button" className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" disabled={!dirty || busy} onClick={() => { setMessage(''); save.mutate(); }}><Save size={16} aria-hidden="true" />{save.isLoading ? 'Saving...' : task?.id === 'company_info' ? 'Save profile' : 'Save changes'}</button>
      <button type="button" className="btn-secondary" disabled={busy} onClick={() => void reload()}>Reload saved values</button>
      <button type="button" className="btn-secondary" disabled={busy} onClick={() => { if (discard()) later.mutate(); }}>Continue later</button>
      <button type="button" className="btn-primary disabled:opacity-50" disabled={busy || dirty || !status.workspace_ready} onClick={() => finish.mutate()}>{finish.isLoading ? 'Finishing...' : 'Complete setup'}</button>
      {status.onboarding_completed && <Link className="underline text-sm" to={home}>Open workspace</Link>}
    </footer>
  </div>;
}
